import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

/**
 * AW-TOOLS-003 -- the original Calendar month grid, as shared,
 * fixture-free presentation (WORKSPACE-FEATURE-001 S14; authoritative
 * original `CalendarToolPanel` at commit 23d500e27).
 *
 * WHY THIS WAS EXTRACTED RATHER THAN REBUILT
 * ------------------------------------------
 * The grid is the half of the Calendar tool that needs no contract at
 * all: a month, its days, forward/back navigation and a selected date.
 * It lived only inside `CalendarToolPanel`, which imports
 * `CALENDAR_FIXTURE` for its appointment defaults and therefore cannot
 * enter Production. Rebuilding the grid beside it would have produced a
 * second calendar to keep in visual sync; extracting it produces one.
 *
 * Every class name, the `classNames` map, the chevron components and
 * the `rounded-2xl border border-white/10 bg-white/5 p-2` container are
 * carried across unchanged from the original, so the Production grid and
 * the development panel are the same pixels from the same source.
 *
 * `selectedDate` and `onSelectDate` are injected. This file imports
 * React, two icons and `react-day-picker` -- no fixture, no projection,
 * no API, no state of its own.
 *
 * WHAT IS DELIBERATELY NOT HERE
 * -----------------------------
 * The appointment schedule list. It is not a styling omission: the
 * workspace cannot tell which prospect an appointment would belong to,
 * so a list here could only be empty or invented. An empty list is the
 * worse of the two -- S7.8 requires unknown and zero to stay
 * distinguishable, and "No appointments scheduled" asserts a zero this
 * surface has not measured. The caller supplies whatever honest
 * statement belongs beneath the grid.
 */
export default function WorkspaceCalendarGrid({
  selectedDate,
  onSelectDate,
  testId = "workspace-calendar-grid",
}) {
  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/5 p-2"
      data-testid={testId}
    >
      <DayPicker
        mode="single"
        selected={selectedDate}
        onSelect={(date) => date && onSelectDate(date)}
        defaultMonth={selectedDate}
        showOutsideDays
        classNames={{
          months: "flex flex-col",
          month: "space-y-2",
          caption: "flex items-center justify-center relative pt-1",
          caption_label: "text-xs font-semibold text-slate-100",
          nav: "flex items-center",
          nav_button: "h-6 w-6 rounded-md bg-white/5 p-0 text-slate-100 opacity-80 hover:opacity-100",
          nav_button_previous: "absolute left-0",
          nav_button_next: "absolute right-0",
          table: "w-full border-collapse",
          head_row: "flex",
          head_cell: "w-8 text-center text-[10px] font-normal text-slate-500",
          row: "flex w-full mt-1",
          cell: "text-center p-0",
          day: "h-8 w-8 rounded-full p-0 text-xs font-normal text-slate-100 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-400",
          day_today: "bg-white/10 text-slate-100",
          day_outside: "text-slate-600 aria-selected:text-slate-600",
          day_selected:
            "bg-indigo-500 text-white hover:bg-indigo-500 hover:text-white focus:bg-indigo-500 focus:text-white",
          day_disabled: "text-slate-600 opacity-50",
        }}
        components={{
          IconLeft: (props) => <ChevronLeft className="h-3.5 w-3.5" {...props} />,
          IconRight: (props) => <ChevronRight className="h-3.5 w-3.5" {...props} />,
        }}
      />
    </div>
  );
}
